<script setup lang="ts">
import { Menu, Monitor, Moon, Search, Sun } from '@lucide/vue'
import { motion } from 'motion-v'
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { cn } from '../lib/cn'
import { pref } from '../lib/theme'
import UiButton from '../ui/Button.vue'
import UiTooltip from '../ui/Tooltip.vue'
import Logo from './Logo.vue'
import UserMenu from './UserMenu.vue'

defineEmits<{ 'open-menu': [] }>()
const router = useRouter()
const q = ref('')

const themes = [
  { value: 'light', icon: Sun, label: 'Light' },
  { value: 'dark', icon: Moon, label: 'Dark' },
  { value: 'system', icon: Monitor, label: 'System' },
] as const

function search() {
  const v = q.value.trim()
  void router.push({ path: '/payments', query: v ? { search: v } : {} })
  q.value = ''
}
</script>

<template>
  <header class="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-border bg-background/70 px-4 backdrop-blur-xl sm:px-6 lg:px-8">
    <UiButton variant="ghost" size="icon" class="lg:hidden" aria-label="Open menu" @click="$emit('open-menu')"><Menu /></UiButton>
    <RouterLink to="/payments" class="lg:hidden"><Logo compact /></RouterLink>

    <form
      class="group flex h-9 w-full max-w-sm items-center gap-2 rounded-lg border border-border bg-card/60 px-3 text-[13px] text-muted-foreground transition-colors focus-within:border-border-strong hover:border-border-strong"
      @submit.prevent="search"
    >
      <Search class="size-4 shrink-0" />
      <input v-model="q" class="min-w-0 flex-1 bg-transparent text-foreground outline-none placeholder:text-muted-foreground" placeholder="Search payments: session, order id, address…" />
    </form>

    <div class="ml-auto flex items-center gap-2">
      <div class="hidden items-center rounded-lg border border-border bg-subtle p-0.5 md:flex" role="radiogroup" aria-label="Theme">
        <UiTooltip v-for="t in themes" :key="t.value" :text="t.label" side="bottom">
          <button
            type="button"
            role="radio"
            :aria-checked="pref === t.value"
            :aria-label="t.label"
            :class="cn('relative flex size-7 items-center justify-center rounded-md transition-colors', pref === t.value ? 'text-foreground' : 'text-muted-foreground hover:text-foreground')"
            @click="pref = t.value"
          >
            <motion.span
              v-if="pref === t.value"
              layout-id="theme-pill"
              class="absolute inset-0 rounded-md border border-border bg-card shadow-card"
              :transition="{ type: 'spring', stiffness: 500, damping: 36 }"
            />
            <component :is="t.icon" class="relative size-3.5" />
          </button>
        </UiTooltip>
      </div>
      <UserMenu />
    </div>
  </header>
</template>
